import type { ContentAdapter, ContentFormData } from "../hooks/useContentEditor";
import type { CrewMember } from "../hooks/useSubmissionMutations";
import type { TitleData } from "../hooks/useTitleMutations";

/**
 * Normalize TitleData to ContentFormData
 */
export function normalizeTitle(title: TitleData): ContentFormData {
  // Parse castData if it exists
  let castArray: string[] = [];
  if (title.castData && Array.isArray(title.castData)) {
    castArray = (title.castData as Array<{ name: string; character?: string }>).map((c) => c.name);
  } else if (title.castNames) {
    castArray = title.castNames;
  }

  // Parse crewData if it exists
  let crewArray: CrewMember[] = [];
  if (title.crewData && Array.isArray(title.crewData)) {
    crewArray = title.crewData as CrewMember[];
  }

  // Extract asset information - prioritize assets with rawVideoKey (uploaded videos)
  // over demo/placeholder assets without rawVideoKey
  const featureAsset = title.assets
    ?.filter((a) => a.kind === "FEATURE")
    .sort((a, b) => {
      // Prioritize assets with rawVideoKey
      const aHasKey = !!a.rawVideoKey;
      const bHasKey = !!b.rawVideoKey;
      if (aHasKey && !bHasKey) return -1;
      if (!aHasKey && bHasKey) return 1;
      return 0;
    })[0];

  const trailerAsset = title.assets
    ?.filter((a) => a.kind === "TRAILER")
    .sort((a, b) => {
      const aHasKey = !!a.rawVideoKey;
      const bHasKey = !!b.rawVideoKey;
      if (aHasKey && !bHasKey) return -1;
      if (!aHasKey && bHasKey) return 1;
      return 0;
    })[0];

  // Helper to get video URL - handle pending encoding status
  const getVideoUrl = (asset: typeof featureAsset): string | undefined => {
    if (!asset) return undefined;

    // Check if hlsUrl has actual content (not empty string)
    if (asset.hlsUrl && asset.hlsUrl.trim() !== "") {
      return asset.hlsUrl;
    }

    // If asset exists but hlsUrl is empty/null, check encoding status
    if (
      asset.encodingStatus === "PENDING" ||
      asset.encodingStatus === "UPLOADED" ||
      asset.encodingStatus === "ENCODING"
    ) {
      return `pending:${asset.id}`;
    }

    return undefined;
  };

  return {
    // Basic info
    title: title.name,
    releaseYear: title.year,
    runtime: title.runtimeMinutes,
    synopsis: title.synopsis,
    description: title.description, // Now properly mapped

    // Credits
    director: title.directorName,
    cast: castArray,
    crew: crewArray,
    genres: title.genres || [],

    // Content details
    rating: title.rating, // Now properly mapped
    language: title.languages || [], // Use languages array for multi-select
    country: title.country,

    // Media
    posterUrl: title.posterUrlHd,
    backdropUrl: title.backdropUrlHd,
    logoUrl: title.logoUrl,
    horizontalTileUrl: title.horizontalTileUrl,
    verticalTileUrl: title.verticalTileUrl,

    // Title-specific fields
    slug: title.slug,
    published: title.published,
    resolution: title.resolution,

    // Assets
    featureAssetId: featureAsset?.id,
    featureVideoUrl: getVideoUrl(featureAsset),
    trailerAssetId: trailerAsset?.id,
    trailerVideoUrl: getVideoUrl(trailerAsset),
  };
}

/**
 * Convert ContentFormData to TitleData update payload
 */
export function contentToTitleUpdate(data: ContentFormData): Partial<TitleData> {
  // Build castData from cast names
  const castData = data.cast?.map((name) => ({
    name,
    character: "",
    profileUrl: null,
  }));

  // Convert language field to languages array
  // language field is now an array from LanguageSelector
  const languages = Array.isArray(data.language) && data.language.length > 0 ? data.language : data.languages || [];

  return {
    name: data.title,
    year: data.releaseYear,
    runtimeMinutes: data.runtime,
    synopsis: data.synopsis,
    description: data.description, // Now properly saved
    rating: data.rating, // Now properly saved
    country: data.country,
    directorName: data.director,
    castNames: data.cast,
    genres: data.genres,
    posterUrlHd: data.posterUrl,
    backdropUrlHd: data.backdropUrl,
    logoUrl: data.logoUrl,
    horizontalTileUrl: data.horizontalTileUrl,
    verticalTileUrl: data.verticalTileUrl,
    languages: languages,
    resolution: data.resolution,
    castData,
    crewData: data.crew,
  };
}

/**
 * Create a Title adapter for the ContentEditor
 */
export function createTitleAdapter(
  title: TitleData | null,
  loading: boolean,
  error: string | null,
  refetch: () => Promise<void>,
  updateTitle: (titleId: string, updates: Partial<TitleData>) => Promise<TitleData>,
  triggerEncoding?: (titleId: string) => Promise<void>,
): ContentAdapter {
  return {
    mode: "title",

    async load(_id: string): Promise<ContentFormData | null> {
      // If still loading, title data will be null - return null and let ContentEditor retry
      if (loading) {
        return null;
      }

      // Only refetch if we don't have data yet
      if (!title && !error) {
        await refetch();
      }

      if (!title) return null;
      return normalizeTitle(title);
    },

    async save(id: string, data: Partial<ContentFormData>): Promise<void> {
      // For partial updates, we need to ensure required fields exist
      if (!data.title) {
        throw new Error("Title name is required");
      }
      const updates = contentToTitleUpdate(data as ContentFormData);
      await updateTitle(id, updates);
    },

    // For titles with pending videos, trigger encoding
    async submit(id: string): Promise<void> {
      if (triggerEncoding) {
        await triggerEncoding(id);
      }
      // After triggering encoding (or if no encoding needed), navigation happens in ContentEditor
    },

    // Admins can always edit published titles
    canEdit(): boolean {
      return true;
    },

    // Get display title
    getTitle(data: ContentFormData): string {
      return data.title;
    },
  };
}
